import type { DialogConfig } from '@svenskaspelab/nova-foundation/nextjs/dialog';
import { marketplaceSharesMw } from '@svenskaspelab/nova-payment-sport';
import { createElement } from 'react';

import type { ActivatedDrawId } from '../../../schema/marketplace/entry.js';

/**
 * Creates a payment confirmation middleware with the provided showDialog function.
 *
 * @param showDialog Dialog function to show confirmation dialog
 * @param marketplaceWallets Record of marketplace wallets with their balance, sold shares, and share price, keyed by ActivatedDrawId
 * @returns Payment confirmation middleware
 */
export function marketplaceShares(
  showDialog: (props: DialogConfig, meta?: string[]) => Promise<void>,
  marketplaceWallets?: Record<ActivatedDrawId, { balance: number; soldShares: number; sharePrice: number }>
): ReturnType<typeof marketplaceSharesMw> {
  return marketplaceSharesMw(
    (paymentContext) =>
      new Promise((resolve, reject) => {
        const { changedBalances } = paymentContext ?? {};
        if (!changedBalances) {
          resolve(); // No balance changes, continue with payment
          return;
        }
        const title = 'Nya andelar har köpts!';
        const messages = ['Ditt saldo har uppdaterats:'];
        for (const { balance, numberOfNewShares, name } of changedBalances) {
          const shareText = numberOfNewShares === 1 ? 'ny andel' : 'nya andelar';
          messages.push(`${name}: ${balance} kr (${numberOfNewShares} ${shareText}).`);
        }
        messages.push(`Du kan gå tillbaka och ändra raden eller fortsätta till betalningen.`);

        showDialog({
          title: title,
          description: messages.map((msg, index) =>
            // eslint-disable-next-line react/no-array-index-key
            createElement('p', { key: index, className: 'text-center lg:text-left' }, msg)
          ),
          buttons: {
            primary: {
              label: 'Fortsätt till betalning',
              action: () => {
                resolve(); // Continue with payment
              },
            },
            secondary: {
              label: 'Avbryt',
              action: () => {
                reject(new Error('Abort payment flow clicked, new shares bought.')); // Cancel payment
              },
            },
          },
        }).catch(() => {
          // If the dialog is closed in another way (ESC, click outside, etc.)
          reject(new Error('Abort payment flow clicked, new shares bought.'));
        });
      }),
    marketplaceWallets
  );
}
